import { useSelector } from "react-redux";
import {
    proposedVisibleRemainingAge,
    selectProposeAffiliation,
    selectProposeBasicInformation,
    selectProposeBasicPlans,
    selectProposeBorrowerAgeOptions,
    selectProposeChanelType,
    selectProposeCommissionValue,
    selectProposeCompanyMobileNumber,
    selectProposeContactPersonMobileNumber,
    selectProposeContent,
    selectProposeCoverageType,
    selectProposeEligibilityAge,
    selectProposeFiles,
    selectProposeFundingArrangements,
    selectProposeGroupClassification,
    selectProposeInputsForLevelRanking,
    selectProposeLoanMaturityMonths,
    selectProposeLoanPortfolio,
    selectProposeMaxAge,
    selectProposeMaxAmount,
    selectProposeMinAge,
    selectProposeMinAmount,
    selectProposeNatureOfBusinessItem,
    selectProposeOtherSpecificationGC,
    selectProposeOtherSpecificationToG,
    selectProposePaymentTerm,
    selectProposeProductType,
    selectProposeProposalAs,
    selectProposeProposalType,
    selectProposePrototypeType,
    selectProposeRidersAmountOfLoans,
    selectProposeRidersForLevelRanking,
    selectProposeRidersForSalaryRank,
    selectProposeRidersForUniform,
    selectProposeRowsForSalaryRank,
    selectProposeSelectedAmountOfLoans,
    selectProposeSelectedBMSR,
    selectProposeSelectedRiders,
    selectProposeSelectedUniformCoverage,
    selectProposeServiceValue,
    selectProposeSubType,
    selectProposeTypeOfGroup,
} from "../../features/propose-data/propose-data-slice";
import { CHANNEL_TYPE_ID, COVERAGE_TYPE_IDS, OTHERS_INPUT_IDS, PRODUCT_TYPE_IDS, PROPOSAL_TYPE_IDS, SUB_TYPE_IDS } from "../../constant/gms-constant";
import { formatNumberWithComma } from "../../utils/format-numbers.utils";

export function useProposedTypeData() {

    // CHANNEL TYPE
    const { channelEmail, channelNumber, channelSubName, selectedChannelBox } = useSelector(selectProposeChanelType);
    const proposeShowInputChanelType =
            selectedChannelBox?.id === CHANNEL_TYPE_ID.broker ||
            selectedChannelBox?.id === CHANNEL_TYPE_ID.generalAgency ||
            selectedChannelBox?.id === CHANNEL_TYPE_ID.agent;

    // PROPOSAL AS (NEW/RENEW)
    const proposedProposalAs = useSelector(selectProposeProposalAs);

    // BASIC INFORMATION
    const proposedBasicInformation = useSelector(selectProposeBasicInformation);
    const proposedNatureOfBusinessItem = useSelector(selectProposeNatureOfBusinessItem);
    const proposeCompanyContactNumber = useSelector(selectProposeCompanyMobileNumber);
    const proposeContactPersonNumber = useSelector(selectProposeContactPersonMobileNumber);

    // AFFILIATIONS
    const proposedAffiliations = useSelector(selectProposeAffiliation);

    // GROUP CLASSIFICATION
    const proposedGroupClassification = useSelector(selectProposeGroupClassification);
    const proposedHasOtherSpecifyGC = proposedGroupClassification?.id === OTHERS_INPUT_IDS.groupClassification
    const proposedOtherSpecifGC = useSelector(selectProposeOtherSpecificationGC)

    // TYPE OF GROUP
    const proposedTypeOfGroup = useSelector(selectProposeTypeOfGroup)
    const proposedOthersTypeOfGroup = useSelector(selectProposeOtherSpecificationToG)
    const proposedTypeOfGroupSubtype = useSelector(selectProposeSubType)
    const proposedIsEmployerEmployeeToG = proposedTypeOfGroup?.id === SUB_TYPE_IDS.employerEmployee
    const proposedIsOFWToG = proposedTypeOfGroup?.id === SUB_TYPE_IDS.OFW
    const proposedHasSubTypeToG =  proposedIsEmployerEmployeeToG || proposedIsOFWToG

    // AGE PROFILE
    const proposedAgeProfile = useSelector(selectProposeEligibilityAge);
    const proposedAgeProfileFormat = proposedAgeProfile?.map(a => a.age).join(' - ') || '';

    // MODE OF PAYMENT
    const proposedModeOfPayment = useSelector(selectProposeFundingArrangements);

    // PROPOSAL TYPE (Customize/Prototype)
    const proposedProposalType = useSelector(selectProposeProposalType);
    const proposedIsPrototype = proposedProposalType?.id === PROPOSAL_TYPE_IDS.prototype
    const proposedIsCustomize = proposedProposalType?.id === PROPOSAL_TYPE_IDS.customized

    // PROTOTYPE TYPE
    const proposedPrototypeType = useSelector(selectProposePrototypeType);

    // PRODUCT TYPE
    const proposedProductType = useSelector(selectProposeProductType);
    const proposedIsProductGPA = proposedProductType?.id === PRODUCT_TYPE_IDS.GPA
    const proposedIsProductGCLI = proposedProductType?.id === PRODUCT_TYPE_IDS.GCLI
    const proposedIsProductGYRT = proposedProductType?.id === PRODUCT_TYPE_IDS.GYRT
    const proposedIsProductGCI = proposedProductType?.id === PRODUCT_TYPE_IDS.GCI
    const proposedIsCoverageType: boolean =  (proposedIsProductGYRT || proposedIsProductGPA || proposedIsProductGCI) 
    const proposedIsAmountOfLoans: boolean =  proposedIsProductGCLI 

    // BASIC PLAN
    const proposedBasicPlan = useSelector(selectProposeBasicPlans);

    // COVERAGE TYPE
    const proposedCoverageType = useSelector(selectProposeCoverageType);
    const proposedIsLevelRanking = proposedCoverageType?.id === COVERAGE_TYPE_IDS.levelRanking
    const proposedIsUniformCoverage = proposedCoverageType?.id === COVERAGE_TYPE_IDS.uniformCoverage
    const proposedIsSalaryRank = proposedCoverageType?.id === COVERAGE_TYPE_IDS.salaryRank
    const proposedIsLevelRankingAndSalaryRank = proposedIsLevelRanking || proposedIsSalaryRank

    // Level Ranking Coverage
    const proposedLevelRankingInputs = useSelector(selectProposeInputsForLevelRanking);
    const proposedLevelRankingRiders = useSelector(selectProposeRidersForLevelRanking);

    // Uniform Coverage
    const proposedUniformInputs = useSelector(selectProposeSelectedUniformCoverage);
    const proposedUniformRiders = useSelector(selectProposeRidersForUniform);

    // Salary Rank Coverage
    const proposedSalaryRankInputs = useSelector(selectProposeRowsForSalaryRank);
    const proposedSalaryRankRiders = useSelector(selectProposeRidersForSalaryRank);

    // Riders 
    const proposedRiders = useSelector(selectProposeSelectedRiders);
    const proposedRiderBMSR = useSelector(selectProposeSelectedBMSR);

    // AMOUNT OF LOANS
    const proposedAmountOfLoans = useSelector(selectProposeSelectedAmountOfLoans);
    const proposedAmountOfLoansRiders = useSelector(selectProposeRidersAmountOfLoans);
    const proposedMinAmountOfLoans = useSelector(selectProposeMinAmount);
    const proposedMaxAmountOfLoans = useSelector(selectProposeMaxAmount);
    const proposedFAmountMin = formatNumberWithComma(proposedMinAmountOfLoans ?? '');
    const proposedFAmountMax = formatNumberWithComma(proposedMaxAmountOfLoans ?? '');
    const proposedFormatMinMaxAmount = `${proposedFAmountMin} - ${proposedFAmountMax}`;
    const proposedAmountOfLoansPortfolio = useSelector(selectProposeLoanPortfolio);
    const proposedFAmountPortfolio = formatNumberWithComma(proposedAmountOfLoansPortfolio ?? '');

    // PAYMENT TERM
    const proposedPaymentTerm = useSelector(selectProposePaymentTerm);

    // LOAN MATURITY
    const proposedLoanMaturityMonths = useSelector(selectProposeLoanMaturityMonths);
    const proposedMaturityMonthsFormat = formatNumberWithComma(proposedLoanMaturityMonths ?? '');

    // COMMISSION / SERVICE FEE
    const proposedCommissionValue = useSelector(selectProposeCommissionValue);
    const proposedServiceValue = useSelector(selectProposeServiceValue);

    // BORROWER AGE
    const prosedBorrowerAgeOptionsList = useSelector(selectProposeBorrowerAgeOptions);
    const proposedMinAge = useSelector(selectProposeMinAge);
    const proposedMaxAge = useSelector(selectProposeMaxAge);
    const proposedRemainingAgeList = useSelector(proposedVisibleRemainingAge).filter(item => item.typeOfAge !== "under_min");

    // SUPPORTING DOCUMENTS
    const proposedFiles = useSelector(selectProposeFiles);

    // NOTES
    const proposedNotes = useSelector(selectProposeContent);

    return {
        // CHANNEL TYPE
        proposedChanelType: selectedChannelBox,
        proposedChannelEmail: channelEmail,
        proposedChannelNumber: channelNumber,
        proposedChannelSubName: channelSubName,
        proposeShowInputChanelType,

        // PROPOSAL AS (NEW/RENEW)
        proposedProposalAs,

        // BASIC INFORMATION
        proposedBasicInformation,
        proposedNatureOfBusinessItem,
        proposeCompanyContactNumber,
        proposeContactPersonNumber,

        // AFFILIATIONS
        proposedAffiliations,

        // GROUP CLASSIFICATION
        proposedGroupClassification,
        proposedHasOtherSpecifyGC,
        proposedOtherSpecifGC,

        // TYPE OF GROUP
        proposedTypeOfGroup,
        proposedOthersTypeOfGroup,
        proposedTypeOfGroupSubtype,
        proposedIsEmployerEmployeeToG,
        proposedIsOFWToG,
        proposedHasSubTypeToG,

        // AGE PROFILE
        proposedAgeProfile,
        proposedAgeProfileFormat,

        // MODE OF PAYMENT
        proposedModeOfPayment,

        // PROPOSAL TYPE (Customize/Prototype)
        proposedProposalType,
        proposedIsPrototype,
        proposedIsCustomize,

        // PROTOTYPE TYPE
        proposedPrototypeType,

        // PRODUCT TYPE
        proposedProductType,
        proposedIsProductGPA,
        proposedIsProductGCLI,
        proposedIsProductGYRT,
        proposedIsProductGCI,
        proposedIsCoverageType,
        proposedIsAmountOfLoans,

        // BASIC PLAN
        proposedBasicPlan,

        // COVERAGE TYPE
        proposedCoverageType,
        proposedIsLevelRanking,
        proposedIsUniformCoverage,
        proposedIsSalaryRank,
        proposedIsLevelRankingAndSalaryRank,

        // Level Ranking Coverage
        proposedLevelRankingInputs,
        proposedLevelRankingRiders,
        // Uniform Coverage
        proposedUniformInputs,
        proposedUniformRiders,
        // Salary Rank Coverage
        proposedSalaryRankInputs,
        proposedSalaryRankRiders,
        
        // Riders 
        proposedRiders,
        proposedRiderBMSR,

        // AMOUNT OF LOANS
        proposedAmountOfLoans,
        proposedAmountOfLoansRiders,
        proposedMinAmountOfLoans,
        proposedMaxAmountOfLoans,
        proposedFAmountMin,
        proposedFAmountMax,
        proposedFormatMinMaxAmount,
        proposedAmountOfLoansPortfolio,
        proposedFAmountPortfolio,

        // PAYMENT TERM
        proposedPaymentTerm,

        // LOAN MATURITY
        proposedLoanMaturityMonths,
        proposedMaturityMonthsFormat,

        // COMMISSION / SERVICE FEE
        proposedCommissionValue,
        proposedServiceValue,

        // BORROWER AGE
        prosedBorrowerAgeOptionsList,
        proposedMinAge,
        proposedMaxAge,
        proposedRemainingAgeList,

        // SUPPORTING DOCUMENTS
        proposedFiles,

        // NOTES
        proposedNotes,
    };
}